import React, { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

import Typography from '@mui/material/Typography';
import { SelectChangeEvent } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';

import Button from '@mui/material/Button';

import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import AccountCircle from '@mui/icons-material/AccountCircle';
import OutlinedInput from '@mui/material/OutlinedInput';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { LoginProps } from '../../interfaces/loginInterface';
import { ApiGetToken } from '../../services/ApiServices';
import { Env } from '../../interfaces/globaleInterface';
import { resetProcessLog } from '../../services/streamingProcessApiService';
import Error from '../../components/Error/Error';
import {
	getAvailablePreviewEnvironments,
	getStoredPreviewNumber,
	PreviewEnvironment,
	setStoredPreviewNumber,
} from '../../services/previewEnvironmentService';
export default function LoginScreen(props: LoginProps) {
	const { successCallBack, changeEnvCallback } = props;
	const [errorUser, setErrorUser] = useState(false);
	const [errorPassword, setErrorPassword] = useState(false);
	const [env, setEnv] = useState<Env>((localStorage.getItem('env') as Env) || Env.DEV);
	const [previews, setPreviews] = useState<PreviewEnvironment[]>([]);
	const [previewNumber, setPreviewNumber] = useState<number | ''>(getStoredPreviewNumber() || '');
	const [previewError, setPreviewError] = useState('');
	const [isLoadingPreviews, setIsLoadingPreviews] = useState(false);
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [message, setMessage] = useState('');
	const [values, setValues] = useState({
		password: '',
		showPassword: false,
	});

	useEffect(() => {
		if (env !== Env.PREVIEW) return;

		let isMounted = true;
		setIsLoadingPreviews(true);
		setPreviewError('');

		getAvailablePreviewEnvironments()
			.then((availablePreviews) => {
				if (!isMounted) return;

				setPreviews(availablePreviews);
				if (!availablePreviews.length) {
					setPreviewNumber('');
					setStoredPreviewNumber(null);
					setPreviewError('Aucune PR Preview n’est disponible.');
					return;
				}

				const storedPreviewNumber = getStoredPreviewNumber();
				if (!availablePreviews.some((preview) => preview.number === storedPreviewNumber)) {
					setPreviewNumber('');
					setStoredPreviewNumber(null);
				}
			})
			.catch(() => {
				if (!isMounted) return;
				setPreviews([]);
				setPreviewNumber('');
				setStoredPreviewNumber(null);
				setPreviewError('Impossible de récupérer les PR Preview.');
			})
			.finally(() => {
				if (isMounted) setIsLoadingPreviews(false);
			});

		return () => {
			isMounted = false;
		};
	}, [env]);

	const login = async () => {
		if (env === Env.PREVIEW && !previewNumber) {
			setPreviewError('Sélectionnez une PR Preview.');
			return;
		}

		if (username && password && env) {
			try {
				setMessage('');
				resetProcessLog();
				await ApiGetToken(env, username, password, previewNumber || null)
					.then((responseJson) => {
						const { token } = responseJson?.data;
						if (responseJson && token) {
							setStoredPreviewNumber(env === Env.PREVIEW ? Number(previewNumber) : null);
							localStorage.setItem('token', token);
							localStorage.setItem('env', env);
							localStorage.setItem('user', JSON.stringify(responseJson.data.user));
							setUsername('');
							setPassword('');
							setMessage('');
							setErrorUser(false);
							setErrorPassword(false);
							successCallBack(env, token);
						} else if (responseJson && responseJson.message) {
							setMessage(responseJson.message);
							setErrorUser(true);
							setErrorPassword(true);
						}
					})
					.catch((message) => {
						return setMessage(message.error);
					});
			} catch (error: any) {
				// Dans le cas ou le serveur ne répond pas du tout ou crash
				setMessage(error.message);
			}
		} else if (username === '') {
			setErrorUser(true);
		} else if (password === '') {
			setErrorPassword(true);
		}
	};

	const handleChangeEnv = (event: SelectChangeEvent) => {
		const envValue = event.target.value as Env;
		if (envValue !== Env.PREVIEW) {
			setPreviewNumber('');
			setPreviewError('');
			setStoredPreviewNumber(null);
		}
		changeEnvCallback(envValue);
		setEnv(envValue);
	};

	const handleChangePreview = (event: SelectChangeEvent<number | ''>) => {
		const selectedPreviewNumber = Number(event.target.value);
		setPreviewNumber(selectedPreviewNumber);
		setStoredPreviewNumber(selectedPreviewNumber);
		setPreviewError('');
	};

	const handleChangeUsername = (event: React.ChangeEvent<HTMLInputElement>) => {
		setUsername(event.target.value);
	};

	const handleChangePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
		setPassword(event.target.value);
	};
	const handleClickShowPassword = () => {
		setValues({
			...values,
			showPassword: !values.showPassword,
		});
	};

	const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
	};

	return (
		<Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
			<Grid container sx={{ spacing: 0, alignItems: "center", justifyContent: "center" }}>
				<Container sx={{ backgroundColor: 'white', width: 500 }}>
					<Grid container direction="column" sx={{ spacing: 0, alignItems: "center", mt: 4 }}>
						<Typography component="h1" variant="h4">
							Connexion
						</Typography>
						<Typography>Viens découvrir le merveilleux mignon petit PHF Dashboard !</Typography>
						{message !== '' && <Error message={message} />}
					</Grid>
					<Grid container direction="column" sx={{ spacing: 0, alignItems: "center", mt: 2 }}>
						<FormControl sx={{ m: 1, minWidth: 120 }}>
							<InputLabel id="env-label">Environnement</InputLabel>
							<Select id={'env-select'} labelId="env-label" sx={{ width: '30ch' }} label="Environnement" onChange={handleChangeEnv} value={env}>
								<MenuItem value={'local'}>Local</MenuItem>
								<MenuItem value={'dev'}>Développement</MenuItem>
								<MenuItem value={'recette'}>Recette</MenuItem>
								<MenuItem value={'prod'}>Production</MenuItem>
								<MenuItem value={'preview'}>PR Preview</MenuItem>
							</Select>
						</FormControl>
						{env === Env.PREVIEW && (
							<FormControl sx={{ mt: 2, width: '30ch' }} error={!!previewError} disabled={isLoadingPreviews}>
								<InputLabel id="preview-label">PR Preview</InputLabel>
								<Select<number | ''>
									id="preview-select"
									labelId="preview-label"
									label="PR Preview"
									value={previewNumber}
									onChange={handleChangePreview}
								>
									{previews.map((preview) => (
										<MenuItem key={preview.number} value={preview.number}>
											PR #{preview.number}
										</MenuItem>
									))}
								</Select>
								{isLoadingPreviews && <CircularProgress size={18} sx={{ position: 'absolute', right: 40, top: 19 }} />}
								{previewError && <FormHelperText>{previewError}</FormHelperText>}
							</FormControl>
						)}
						<TextField
							id={errorUser ? 'outlined-error' : 'input-with-icon-textfield'}
							error={errorUser}
							label="Nom d'utilisateur"
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<AccountCircle />
									</InputAdornment>
								),
							}}
							variant="outlined"
							sx={{ mt: 2, width: '30ch' }}
							value={username}
							onChange={handleChangeUsername}
						/>
						<Box sx={{ height: 20 }} />
						<FormControl sx={{ m: 1, width: '30ch' }} variant="outlined">
							<InputLabel color={errorPassword ? 'error' : 'primary'} htmlFor="outlined-adornment-password">
								Mot de passe
							</InputLabel>
							<OutlinedInput
								id={errorPassword ? 'outlined-error' : 'outlined-adornment-password'}
								error={errorPassword}
								type={values.showPassword ? 'text' : 'password'}
								value={password}
								onChange={handleChangePassword}
								label="Mot de passe"
								endAdornment={
									<InputAdornment position="start">
										<IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="start">
											{values.showPassword ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									</InputAdornment>
								}
							/>
						</FormControl>
						<Box sx={{ height: 20 }} />
						<Button
							sx={{ width: 200, height: 50 }}
							color="primary"
							variant="contained"
							onClick={login}
							disabled={env === Env.PREVIEW && (isLoadingPreviews || !previewNumber)}
						>
							Connexion
						</Button>
						<Box sx={{ height: 20 }} />
					</Grid>
				</Container>
			</Grid>
		</Container>
	);
}
